// public/js/features/assets/route-modal.js
// Full-screen modal showing one asset's historical route on a MapLibre map —
// rendered exactly like the map page (route line, trip/stop markers, accuracy,
// clickable points), but scoped to a single asset and without the map page's
// other controls (only the base-layer switcher is kept).
//
// Deliberately self-contained: it owns its own map instance and is meant to be
// expanded later (time scrubber, per-trip filtering, playback, …).
import { BASE_LAYERS } from '../../modules/map/constants.js';
import { setBaseStyle, getInitialStyle, getStoredBaseLayerId } from '../../modules/map/core.js';
import { loadAssetHistoryAndDrawRoute, clearRoute } from '../../modules/map/history.js';
import { formatAssetLabel } from '../../utils/asset-utils.js';
import { renderPoiOverlays } from '../pois/poi-overlay-layer.js';
import { ensurePoiData } from '../pois/poi-data.js';

let routeMap = null;

// Custom POI map overlays on the detail fullscreen map (docs/poi-custom-maps.md):
// same shared module as the main map, so they render one level above the base
// map and below the route. Re-run after a base-layer switch (setStyle wipes it).
function addRouteOverlays() {
  if (!routeMap) return;
  ensurePoiData().then(() => renderPoiOverlays(routeMap)).catch(() => {});
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

export function openRouteModal(asset) {
  if (!asset) return;
  const id = 'asset-route-modal';
  document.getElementById(id)?.remove();

  const layerOpts = BASE_LAYERS.map(l =>
    `<option value="${l.id}">${esc(l.label || l.id)}</option>`).join('');

  const html = `
    <div class="modal fade" id="${id}" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-fullscreen">
        <div class="modal-content">
          <div class="modal-header py-2">
            <h5 class="modal-title mb-0"><i class="fa-solid fa-route me-2"></i>Route — ${esc(formatAssetLabel(asset))}</h5>
            <div class="ms-auto d-flex align-items-center gap-2">
              <label class="small text-muted mb-0 d-none d-sm-inline">Base map</label>
              <select id="route-base-layer" class="form-select form-select-sm" style="max-width:220px;">${layerOpts}</select>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
          </div>
          <div class="modal-body p-0" style="position:relative;overflow:hidden;">
            <div id="asset-route-map" style="position:absolute;inset:0;"></div>
            <div id="route-modal-status" class="position-absolute top-50 start-50 translate-middle text-muted small"></div>
          </div>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', html);
  const el = document.getElementById(id);
  const modal = new bootstrap.Modal(el);
  const statusEl = el.querySelector('#route-modal-status');
  const setStatus = (t) => { if (statusEl) statusEl.textContent = t || ''; };

  const drawRoute = (fit = true) => {
    if (!routeMap) return;
    setStatus('Loading route…');
    loadAssetHistoryAndDrawRoute(asset.assetUUID, routeMap, { fit })
      .then(r => setStatus(r === 'empty' ? 'No route data in the last 14 days.' : ''))
      .catch(() => setStatus('Could not load route.'));
  };

  el.addEventListener('shown.bs.modal', () => {
    if (typeof maplibregl === 'undefined') { setStatus('Map library not available.'); return; }
    routeMap = new maplibregl.Map({
      container: 'asset-route-map',
      style: getInitialStyle(),
      center: [parseFloat(asset.lastLon) || 8.2, parseFloat(asset.lastLat) || 46.8],
      zoom: 11,
      attributionControl: true,
    });
    routeMap.addControl(new maplibregl.NavigationControl({ showCompass: true }), 'top-right');
    routeMap.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');
    routeMap.once('load', () => { routeMap.resize(); drawRoute(true); addRouteOverlays(); });

    // Base-layer switcher — setStyle wipes custom layers, so redraw on ready.
    const sel = el.querySelector('#route-base-layer');
    if (sel) {
      sel.value = getStoredBaseLayerId() || BASE_LAYERS[0].id;
      sel.addEventListener('change', () => setBaseStyle(routeMap, sel.value, () => { drawRoute(false); addRouteOverlays(); }));
    }
  }, { once: true });

  el.addEventListener('hidden.bs.modal', () => {
    try { if (routeMap) { clearRoute(routeMap); routeMap.remove(); } } catch (e) {}
    routeMap = null;
    el.remove();
  }, { once: true });

  modal.show();
}
